import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, useNavigate, Navigate, useParams } from 'react-router-dom'
import { DashboardLayout, UserPreferencesProvider, LanguageSync, GlobalPreferencesSync } from '@airclou/shell'
import { I18nextProvider } from 'react-i18next'
import i18n from './i18n'
import { useTranslation } from 'react-i18next'
import { ToastContainer, ToastProvider, useToastContext } from '@airclou/ui'
import type { BrandingConfig, ProfileMenuItem } from '@airclou/shell'
import {
  LifeBuoy,
  IdCard,
} from 'lucide-react'
import { buildNavigation } from './navigation'
import DashboardSearch from './pages/search/DashboardSearch'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { AuthGuard } from './components/auth/AuthGuard'
import { SSOManagement } from '@/pages/settings/SSOManagement'
import { AccountPage } from './pages/account/AccountPage'
import { OrganizationPage } from './pages/settings/OrganizationPage'
import { SecurityPage } from './pages/settings/security'
import { BillingOverviewPage } from './pages/billing/BillingOverviewPage'
import { OverviewPage } from './pages/dashboard/OverviewPage'
import { CompaniesPage } from './pages/hierarchy/CompaniesPage'
import { CompanyDetailsPage } from './pages/hierarchy/CompanyDetailsPage'
import { GroupDetailsPage } from './pages/hierarchy/GroupDetailsPage'
import { WorkspaceDetailsPage } from './pages/hierarchy/WorkspaceDetailsPage'
import { WorkspaceUserDetailsPage } from './pages/hierarchy/WorkspaceUserDetailsPage'
import { WorkspacesPage } from './pages/workspaces/WorkspacesPage'
import { AppsPage, AppDetailsPage } from './pages/apps'
import { AuditLogsPage } from './pages/organization/AuditLogsPage'
import { AcceptInvitationPage } from './pages/AcceptInvitationPage'
import { PeoplePage } from './pages/people/PeoplePage'
import { TeamMemberDetailsPage } from './pages/team/TeamMemberDetailsPage'
import { SupportPage } from './pages/support/SupportPage'
import { NotFoundPage } from './pages/NotFoundPage'

// Activity tracker for session management
import { activityTracker, startSessionTracking, stopSessionTracking, shouldShowSessionDebugPanel } from './utils/sessionManager';
import { SessionDebugPanel, SessionStatus } from '@airclou/auth-react';

// Redirect components for legacy routes with parameters
function RedirectCompanyDetails() {
  const { id } = useParams()
  return <Navigate to={`/companies/${id}`} replace />
}

/**
 * `/settings/account/:tab` → `/account/:tab`. Only Preferences survived the
 * merge as a tab — the old "Basic" tab's identifiers now sit at the foot of
 * Profile — so anything else lands on the page itself.
 */
function RedirectAccountTab() {
  const { tab } = useParams()
  return <Navigate to={tab === 'preferences' ? '/account/preferences' : '/account'} replace />
}

function RedirectWorkspaceDetails() {
  const { id, tab } = useParams()
  const path = tab 
    ? `/workspaces/${id}/${tab}`
    : `/workspaces/${id}`
  return <Navigate to={path} replace />
}

function AppContent() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toasts } = useToastContext();
  const { t } = useTranslation('dashboard');

  // Initialize activity tracker for session management
  useEffect(() => {
    if (user) {
      startSessionTracking();
      return () => {
        stopSessionTracking();
      };
    }
  }, [user]);

  // Define branding configuration like Playbook
  const brandingConfig: BrandingConfig = {
    name: 'Dashboard',
    logoText: 'D',
    logoClassName: ''
  }

  // Sidebar IA and its role gating live in ./navigation. Gated on the org role
  // (`tenant_users`), which is what the backend's write gates resolve — not
  // `user.role`, the app-access role, which says 'user' for an org admin and
  // hid Billing and Settings from them.
  const { mainNavigation, expandableItems } = buildNavigation(user?.orgRole ?? undefined, t)

  const handleSearch = (query: string) => {
    // Navigate to search page with query parameter
    navigate(`/search?q=${encodeURIComponent(query)}`);
  }

  // Transform user data for navbar (use real user data from AuthContext)
  const navbarUsers = user ? [{
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role,
    jobTitle: user.role === 'owner' ? t('roles.owner') : user.role === 'admin' ? t('roles.admin') : user.role === 'billing_admin' ? t('roles.billingAdmin') : t('roles.viewer')
  }] : []

  // Profile menu items for Dashboard app
  const profileMenuItems: ProfileMenuItem[] = [
    // One entry, not two. Profile and Account were separate items pointing at
    // two pages that both described the same person; they are one page now.
    {
      id: 'account',
      label: t('nav.account'),
      icon: IdCard,
      onClick: () => navigate('/account')
    },
    {
      id: 'help-support',
      label: t('nav.helpSupport'),
      icon: LifeBuoy,
      onClick: () => navigate('/support')
    }
  ]

  return (
    <>
      {/* Inside the auth guard, not up beside the provider: the account-wide
          preferences need a session, and the provider deliberately sits above
          one so the public invitation page is themed. Keep it outside any
          wrapper whose children can change position, which would remount it —
          and a remount is a sign-out as far as the sync is concerned. */}
      <GlobalPreferencesSync userId={user?.id} />
      <DashboardLayout
        branding={brandingConfig}
        navigation={mainNavigation}
        documentSections={[]}
        expandableItems={expandableItems}
        bottomNavigation={[]}
        dividerBeforeExpandable
        contentClassName="p-8 lg:pt-6 pt-20"
        navbarProps={{
          users: navbarUsers,
          currentUserId: user?.id || '',
          onUserChange: (userId: string) => console.log('User changed:', userId),
          playbookOwner: user?.name || 'User',
          onSearch: handleSearch,
          // Dashboard's results page is /search, not the shell's default
          // /dashboard/search — without this the navbar box stops tracking the
          // URL, so Back out of a result leaves the previous name in it.
          syncUrlPaths: ['/search'],
          hideUserSwitcher: true,
          branding: brandingConfig,
          profileMenuItems
        }}
      >
        <Routes>
          <Route path="/" element={<OverviewPage />} />
          <Route path="/search" element={<DashboardSearch />} />
          <Route path="/support" element={<SupportPage />} />
          
          {/* Top-level Navigation Routes */}
          <Route path="/people" element={<PeoplePage />} />
          {/* Member detail (drill-in from the People list). Declared before
              /people/:tab so the literal "user" segment wins over :tab. */}
          <Route path="/people/user/:userId" element={<TeamMemberDetailsPage />} />
          <Route path="/people/user/:userId/workspaces" element={<TeamMemberDetailsPage />} />
          <Route path="/people/:tab" element={<PeoplePage />} />
          <Route path="/workspaces" element={<WorkspacesPage />} />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/apps/:id" element={<AppDetailsPage />} />
          
          {/* Legacy Team Routes - redirect to People */}
          <Route path="/team" element={<Navigate to="/people" replace />} />
          <Route path="/team/invitations" element={<Navigate to="/people/invited" replace />} />
          <Route path="/team/:memberId" element={<Navigate to="/people" replace />} />
          <Route path="/team/:memberId/workspaces" element={<Navigate to="/people" replace />} />
          <Route path="/team/user/:userId" element={<Navigate to="/people" replace />} />
          <Route path="/team/user/:userId/workspaces" element={<Navigate to="/people" replace />} />
          
          {/* Organization Routes (admin-only) */}
          <Route path="/settings" element={<OrganizationPage />} />
          <Route path="/companies" element={<CompaniesPage />} />
          <Route path="/companies/:id" element={<CompanyDetailsPage />} />
          <Route path="/companies/:id/:tab" element={<CompanyDetailsPage />} />
          <Route path="/companies/:id/groups/:groupId" element={<GroupDetailsPage />} />
          <Route path="/workspaces/:id" element={<WorkspaceDetailsPage />} />
          <Route path="/workspaces/:id/overview" element={<WorkspaceDetailsPage />} />
          <Route path="/workspaces/:id/users" element={<WorkspaceDetailsPage />} />
          <Route path="/workspaces/:id/groups" element={<WorkspaceDetailsPage />} />
          <Route path="/workspaces/:workspaceId/users/:userId" element={<WorkspaceUserDetailsPage />} />
          <Route path="/workspaces/:workspaceId/users/:userId/:tab" element={<WorkspaceUserDetailsPage />} />
          <Route path="/workspaces/:id/applications" element={<WorkspaceDetailsPage />} />
          <Route path="/workspaces/:id/settings" element={<WorkspaceDetailsPage />} />
          <Route path="/billing" element={<BillingOverviewPage />} />
          <Route path="/billing/:tab" element={<BillingOverviewPage />} />
          <Route path="/sso" element={<SSOManagement />} />
          <Route path="/sso/:tab" element={<SSOManagement />} />
          <Route path="/security" element={<SecurityPage />} />
          <Route path="/security/:tab" element={<SecurityPage />} />
          <Route path="/audit" element={<AuditLogsPage />} />
          
          {/* Legacy route redirects for backwards compatibility */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/dashboard/*" element={<Navigate to={window.location.pathname.replace('/dashboard', '')} replace />} />
          <Route path="/hierarchy/companies" element={<Navigate to="/companies" replace />} />
          <Route path="/hierarchy/companies/:id" element={<RedirectCompanyDetails />} />
          <Route path="/hierarchy/workspaces/:id" element={<RedirectWorkspaceDetails />} />
          <Route path="/hierarchy/workspaces/:id/:tab" element={<RedirectWorkspaceDetails />} />
          <Route path="/settings/organization" element={<Navigate to="/settings" replace />} />
          <Route path="/settings/security" element={<Navigate to="/security" replace />} />
          <Route path="/settings/security/:tab" element={<Navigate to="/security" replace />} />
          <Route path="/settings/sso" element={<Navigate to="/sso" replace />} />
          <Route path="/settings/sso/:tab" element={<Navigate to="/sso" replace />} />
          {/* Personal settings. Deliberately not under /settings — that is the
              organization page, and a per-user page nested beneath it made the
              sidebar claim the operator was in org settings. */}
          <Route path="/account" element={<AccountPage />} />
          <Route path="/account/:tab" element={<AccountPage />} />
          <Route path="/settings/profile" element={<Navigate to="/account" replace />} />
          <Route path="/settings/account" element={<Navigate to="/account" replace />} />
          <Route path="/settings/account/:tab" element={<RedirectAccountTab />} />
          
          {/* Legacy subscription routes - redirect to billing */}
          <Route path="/subscription" element={<Navigate to="/billing" replace />} />
          <Route path="/subscription/usage" element={<Navigate to="/billing" replace />} />
          {/* Add-ons never got their own tab, so this lands on Subscription
              rather than on a /billing/addons that bounces to the overview. */}
          <Route path="/subscription/addons" element={<Navigate to="/billing/subscription" replace />} />
          
          {/* Unmatched routes get a real not-found page. This used to render
              OverviewPage, which made every broken link look like a working
              link home — that is how /support stayed dead unnoticed. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </DashboardLayout>

      <ToastContainer toasts={toasts} position="top-right" />

      {/* The debug panel is fine inside the guard; the warning is not — see the
          sibling mount in App() below. */}
      {shouldShowSessionDebugPanel() && <SessionDebugPanel activityTracker={activityTracker} />}
    </>
  )
}

export default function App() {
  return (
    <I18nextProvider i18n={i18n}>
      {/*
        `UserPreferencesProvider` is the only thing in the suite that puts the
        `dark` class on <html>. Dashboard never mounted it, so every `dark:`
        class in this app — and the whole dark palette in base.css — was dead:
        the styles were correct and nothing could ever trigger them.

        It wraps the router rather than sitting inside the auth guard so the
        public invitation page is themed too. Dashboard has no preferences table
        of its own, so there is no `onSave` — the choice persists through the
        account-wide layer instead (`GlobalPreferencesSync`, mounted inside the
        guard), which is also what makes it follow the user to their other
        devices and to the suite's other apps.
      */}
      <UserPreferencesProvider appSlug="dashboard">
        <LanguageSync i18n={i18n} />
        <ToastProvider>
          <BrowserRouter>
            <Routes>
              {/* Public route - outside AuthGuard (user may not have account yet) */}
              <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
            
              {/* Protected routes - require authentication */}
              <Route path="/*" element={
                <AuthProvider>
                  <AuthGuard>
                    <AppContent />
                  </AuthGuard>
                  {/* A sibling of the guard, not a child of it. An idle sign-out sets
                      the user to null and waits ~1.2s so this popover can explain the
                      redirect — but ProtectedRoute renders null on that same update,
                      so mounted inside the guard the warning unmounted exactly when
                      it was needed and dashboard users got a blank page instead.
                      Every other app already mounts it as a sibling. */}
                  <SessionStatus devOnly={false} />
                </AuthProvider>
              } />
            </Routes>
          </BrowserRouter>
        </ToastProvider>
      </UserPreferencesProvider>
    </I18nextProvider>
  )
}
